import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { SizeMe } from 'react-sizeme';
import { LoadingData, NoData } from '@hwm/ui-lib';
import { FilterType, SortOrderType, SortColumnType, SearchType } from '@shared/models';
import { useSitesDataSource } from './sitesDataSource';
import { SiteListFilters } from './SiteListFilters';
import { InfiniteScrollList } from './InfiniteScrollList';
import { Fade } from '@material-ui/core';
import { useAppSelector } from '@core/store/store';
import { useSiteListState } from './useSiteListFiltersState';
import { selectedAccStateSelector, userMainAccStateSelector } from '@core/store/library-stores/selectors';
import { NoDataInformationType, noDataInformationOptionsFn } from '@shared/components';

export const LIST_ITEM_HEIGHT = 88;

const HEIGHT_FACTOR = 16;
const MAX_HEIGHT = 280;

export const SiteList: React.FC = () => {
  const [page, setPage] = useState(1);

  const {
    siteListState: { search, searchType, filter, sortOrder, columnSort, zone },
  } = useSiteListState();

  const { hierarchy } = useAppSelector(selectedAccStateSelector);
  const { useZones } = useAppSelector(userMainAccStateSelector);
  const { selectedSitesList } = useAppSelector((state) => state.sites);

  const {
    totalAll,
    totalSelected,
    allSites,
    selectedSites,
    loading,
    handleGetAllSites,
    handleGetSelectedSites,
    handleSetEmptySitesData,
  } = useSitesDataSource();

  const handleDefaultState = useCallback(() => {
    setPage(1);
    handleSetEmptySitesData();
  }, [handleSetEmptySitesData]);

  const handleGetSites = useCallback(
    (
      page: number,
      filter: FilterType,
      search?: string,
      sortOrder?: SortOrderType,
      columnSort?: SortColumnType,
      searchType?: SearchType,
      zone?: string
    ) => {
      if (filter === FilterType.All) {
        handleGetAllSites(page, search, sortOrder, columnSort, searchType, useZones ? zone : null);
      } else {
        handleGetSelectedSites(search, sortOrder, columnSort, searchType, useZones ? zone : null);
      }
    },
    [useZones, handleGetAllSites, handleGetSelectedSites]
  );

  useEffect(() => {
    handleDefaultState();
  }, [selectedSitesList, filter, search, sortOrder, columnSort, searchType, zone, handleDefaultState]);

  useEffect(() => {
    handleGetSites(page, filter, search, sortOrder, columnSort, searchType, zone);
  }, [page, filter, search, sortOrder, columnSort, searchType, zone, handleGetSites]);

  const siteListLoading = useMemo(
    () => (filter === FilterType.All ? loading && page === 1 : loading),
    [filter, loading, page]
  );

  const showSiteList = useMemo(
    () => (filter === FilterType.All ? totalAll > 0 : totalSelected > 0),
    [filter, totalAll, totalSelected]
  );

  const showNoData = useMemo(
    () => (filter === FilterType.All ? totalAll === 0 : totalSelected === 0),
    [filter, totalAll, totalSelected]
  );

  const noDataInformation = useMemo(() => {
    return filter === FilterType.Selected
      ? noDataInformationOptionsFn()[NoDataInformationType.EmptySelectedSites]
      : hierarchy?.length > 1
        ? noDataInformationOptionsFn()[NoDataInformationType.SelectDifferentAccount]
        : '';
  }, [filter, hierarchy?.length]);

  const siteListComponent = useCallback(
    (page: number, filter: FilterType, height: number) => {
      return (
        <Fade in={showSiteList} {...{ timeout: 800 }}>
          <div
            style={{
              minHeight: 180,
              maxHeight: (height || MAX_HEIGHT) - HEIGHT_FACTOR,
            }}
          >
            <InfiniteScrollList
              data={(filter === FilterType.All ? allSites : selectedSites) ?? []}
              currentPage={page}
              setCurrentPage={setPage}
              listHeight={(height || MAX_HEIGHT) - HEIGHT_FACTOR}
              totalItems={filter === FilterType.All ? totalAll : totalSelected}
              childHeight={LIST_ITEM_HEIGHT}
            />
          </div>
        </Fade>
      );
    },
    [totalAll, totalSelected, allSites, selectedSites, showSiteList]
  );

  return (
    <div className="flex flex-col h-full w-full justify-start relative">
      <SiteListFilters loading={siteListLoading} handleDefaultState={handleDefaultState} />
      {siteListLoading ? (
        <div
          className="absolute top-1/2 left-1/2"
          style={{
            transform: 'translate(-50%,-50%)',
          }}
        >
          <LoadingData />
        </div>
      ) : showNoData ? (
        <NoData additionalText={noDataInformation} showCompanyLogo />
      ) : null}

      <SizeMe monitorHeight>
        {({ size: { height } }) => (
          <div className="flex h-full w-full">{showSiteList && siteListComponent(page, filter, height)}</div>
        )}
      </SizeMe>
    </div>
  );
};
